import { useEffect, useRef, useState, memo } from "react";
import styles from "./TradeMarkers.module.css";
import { Order } from "entities/order";
import { COLORS, GRID, type ZoomRange } from "features/chart";
import { useSelector } from "react-redux";
import { RootState } from "store";
import websocketService from "services/websocket";

interface Props {
  orders: Order[];
  symbol: string;
  getTimeX: (candleIndex: number) => number | null;
  getPriceY: (price: number) => number | null;
  findCandleIndex: (timestamp: number) => number | null;
  zoomRange: ZoomRange;
  timeToX: (timestamp: number) => number | null;
  layoutVersion: number;
}

// Мемоизированная марка для открытых ордеров
const OpenMarker = memo(
  ({ order, x, y }: { order: Order; x: number; y: number }) => {
    return (
      <div className={styles.marker} style={{ left: `${x}px`, top: `${y}px` }}>
        <div
          className={`${styles.card} ${order.type === "buy" ? styles.call : styles.put}`}
        >
          <div className={styles.container}>
            <div className={styles.amount}>
              <span>{order.type === "buy" ? "▲" : "▼"}</span>${order.amount}
            </div>
          </div>
        </div>
        <div
          className={`${styles.point} ${order.type === "buy" ? styles.call : styles.put}`}
        />
      </div>
    );
  },
  (prev, next) => prev.x === next.x && prev.y === next.y,
);

// Мемоизированная марка для закрытых ордеров
const ClosedMarker = memo(
  ({ order, x, y }: { order: Order; x: number; y: number }) => {
    const result = order.profit !== undefined ? order.amount + order.profit : 0;
    const isWin = result > 0;

    return (
      <div className={styles.marker} style={{ left: `${x}px`, top: `${y}px` }}>
        <div
          className={`${styles.resultCard} ${isWin ? styles.win : styles.lose}`}
        >
          {isWin ? `+$${result.toFixed(2)}` : "$0"}
        </div>
        <div
          className={`${styles.point} ${isWin ? styles.win : styles.lose}`}
        />
      </div>
    );
  },
  (prev, next) => prev.x === next.x && prev.y === next.y,
);

export const TradeMarkers = ({
  orders,
  symbol,
  getTimeX,
  getPriceY,
  findCandleIndex,
  zoomRange,
  timeToX,
  layoutVersion,
}: Props) => {
  void zoomRange; // used only to trigger re-render on zoom/pan
  void layoutVersion; // used only to trigger re-render on layout resize
  const [closedMarkers, setClosedMarkers] = useState<Order[]>([]);
  const orderHistory = useSelector(
    (state: RootState) => state.trading.orderHistory,
  );

  // const graphReadyTime = useRef(Date.now());

  const graphReadyTime = useRef(websocketService.getCurrentServerTime());

  const prevIdsRef = useRef<Set<string>>(new Set());


  useEffect(() => {
    orderHistory.forEach((order) => {
      if (order.symbol !== symbol) return;
      if (prevIdsRef.current.has(order.id)) return;

      // Показываем только новые ордера, закрытые после графика
      const closeTime = order.closeTimestamp ?? order.timestamp;
      if (closeTime < graphReadyTime.current) return;

      prevIdsRef.current.add(order.id);
      setClosedMarkers((m) => [...m, order]);

      setTimeout(() => {
        setClosedMarkers((m) => m.filter((x) => x.id !== order.id));
      }, 10000);
    });
  }, [orderHistory, symbol]);

  return (
    <>
      {orders.map((order) => {
        if (order.closeTimestamp! < order.timestamp) {
          return null;
        }

        if (order.symbol !== symbol || order.status !== "open") return null;

        const candleIndex = findCandleIndex(order.timestamp);
        if (candleIndex === null) return null;

        const x1 = getTimeX(candleIndex);
        const x2 = timeToX(order.closeTimestamp!);
        const y = getPriceY(order.openPrice);

        if (x1 === null || x2 === null || y === null) return null;
        if (x2 < x1) return null;

        return (
          <div key={order.id}>
            {/* горизонтальная линия */}
            <div
              className={`${styles.horizontalLine} ${order.type === "buy" ? styles.buyLine : styles.sellLine}`}
              style={{
                left: `${x1}px`,
                top: `${y}px`,
                width: `${x2 - x1}px`,
              }}
            />
            {/* вертикальная линия */}
            <div
              className={styles.verticalLine}
              style={{
                backgroundColor: COLORS.line,
                left: `${x2}px`,
                top: `${GRID.top}px`,
                bottom: `${GRID.bottom}px`,
              }}
            />
            <div
              className={styles.flag}
              style={{
                ["--flag-bg" as any]: COLORS.grid,
                ["--flag-border" as any]: COLORS.line,
                left: `${x2}px`,
                top: `${GRID.top}px`,
              }}
            />
            <OpenMarker order={order} x={x1} y={y} />
          </div>
        );
      })}

      {closedMarkers.map((order) => {
        if (order.symbol !== symbol) return null;

        const closeTime = order.closeTimestamp ?? order.timestamp;
        const closePrice = order.closePrice ?? order.openPrice;

        const candleIndex = findCandleIndex(closeTime);
        if (candleIndex === null) return null;

        const x = getTimeX(candleIndex);
        const y = getPriceY(closePrice);
        if (x === null || y === null) return null;

        return (
          <ClosedMarker key={`closed-${order.id}`} order={order} x={x} y={y} />
        );
      })}
    </>
  );
};
